/**
 * useSlashLookup — Hook that detects an active slash-command (`/`) token in a
 * textarea's value (based on the current caret position) and produces a
 * two-stage UI state for the slash-command dropdown:
 *
 *   stage 1 ("category"): the user has just typed `/` and is choosing a
 *     category (e.g. `Entity Name`, `Time Period`).
 *   stage 2 ("search"):   the user has picked a category, and the dropdown
 *     shows fuzzy-ranked items from that category.
 *
 * A slash token is "active" when, walking LEFT from the caret, the nearest
 * `/` has its left neighbour as whitespace OR is at the very start of the
 * string. Whitespace is allowed inside the query itself (so the user can
 * search for entities like "Coca Cola" with a literal space) — the dropdown
 * is closed by Enter / Tab / mouse selection or by Escape, not by a space.
 *
 * Stage transitions:
 *   - A new slash token always starts in 'category' mode.
 *   - `pickCategory(id)` moves to 'search' mode for that category.
 *   - If the user types characters after `/` while still in 'category' mode
 *     (i.e. without explicitly picking), the hook auto-picks the first
 *     category whose `id` is `'entity'` (or the first category) so the legacy
 *     single-stage UX `/cor` → entity search keeps working.
 *   - The "query" passed to the search is the substring between the `/` and
 *     the caret. `accept` replaces exactly that range with `/{entity_name}`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { LookupItem, ScoredMatch, searchLookup } from './lookupSearchAlgo';
import type { LookupCategory } from '../AskAsperAgentic.types';

export interface SlashRange {
    /** Inclusive start position of the slash character. */
    start: number;
    /** Exclusive end position (current caret). */
    end: number;
}

export type SlashLookupMode = 'category' | 'search';

export interface UseSlashLookupResult {
    /** True when an active slash token is detected and the dropdown should render. */
    isActive: boolean;
    /** Which stage we're in. `'category'` shows the picker; `'search'` shows ranked items. */
    mode: SlashLookupMode;
    /** Text the user has typed between `/` and the caret (empty when token is just `/`). */
    query: string;
    /** Replacement range over the input value, or null if no active token. */
    range: SlashRange | null;
    /** Ordered list of categories shown in `mode === 'category'`. */
    categories: LookupCategory[];
    /** Id of the picked category (null when in `'category'` mode). */
    activeCategoryId: string | null;
    /** Human-readable label of the picked category (null when in `'category'` mode). */
    activeCategoryLabel: string | null;
    /** Top-N ranked lookup matches for the current query, scoped to the active category. */
    suggestions: ScoredMatch[];
    /** Index of the currently highlighted row (clamped to the active list length-1). */
    highlightIndex: number;
    setHighlightIndex: (idx: number) => void;
    /** Promote a category to the search stage. No-op if the id isn't in `categories`. */
    pickCategory: (categoryId: string) => void;
    /**
     * Compute the new textarea value/caret when a suggestion is picked. The
     * insertion is the plain entity name (the leading `/` *and* the in-flight
     * query are dropped) so the visible input contains no slash for confirmed
     * picks. The returned `range` is the inclusive/exclusive bounds of the
     * inserted entity name within `nextValue`, suitable for pushing into the
     * caller's selected-ranges list. Returns null when there is no active
     * slash token (e.g. dropdown was already closed).
     */
    accept: (
        item: LookupItem,
    ) => {
        nextValue: string;
        nextCaret: number;
        item: LookupItem;
        range: { start: number; end: number };
    } | null;
    /** Close the dropdown for the current token without clearing the input (Esc). */
    close: () => void;
}

interface SlashTokenInfo {
    slashPos: number;
    query: string;
    range: SlashRange;
}

const WHITESPACE_RE = /\s/;
/** Default number of suggestions to compute per category. The dropdown caps the
 *  *visible* rows to roughly 10 and scrolls the remainder. */
const DEFAULT_MAX_RESULTS = 30;

/** Static items used by the default Time Period category. The host can override the
 *  set by passing its own `lookupCategories` to the hook. */
export const DEFAULT_TIME_PERIOD_ITEMS: LookupItem[] = [
    { column_name: 'Time Period', entity_name: '4 Weeks' },
    { column_name: 'Time Period', entity_name: '12 Weeks' },
    { column_name: 'Time Period', entity_name: '26 Weeks' },
    { column_name: 'Time Period', entity_name: '52 Weeks' },
    { column_name: 'Time Period', entity_name: 'YTD' },
];

/** Default ordered category list surfaced in the slash-command picker.
 *  The `Entity Name` category sources items from the runtime `lookupItems`
 *  prop, while `Time Period` ships with a static set of relative windows. */
export const DEFAULT_LOOKUP_CATEGORIES: LookupCategory[] = [
    { id: 'entity', label: 'Entity Name' },
    { id: 'time_period', label: 'Time Period', items: DEFAULT_TIME_PERIOD_ITEMS },
];

function detectSlashToken(value: string, caretPos: number): SlashTokenInfo | null {
    if (caretPos < 0 || caretPos > value.length) return null;
    for (let i = caretPos - 1; i >= 0; i--) {
        if (value[i] !== '/') continue;
        if (i === 0 || WHITESPACE_RE.test(value[i - 1])) {
            const query = value.slice(i + 1, caretPos);
            return { slashPos: i, query, range: { start: i, end: caretPos } };
        }
    }
    return null;
}

export interface UseSlashLookupOptions {
    /** Ordered list of categories shown in the picker. Defaults to `DEFAULT_LOOKUP_CATEGORIES`. */
    categories?: LookupCategory[];
    /** Maximum number of ranked suggestions returned per query. Default 30. */
    maxResults?: number;
}

export function useSlashLookup(
    value: string,
    caretPos: number,
    items: LookupItem[] | undefined,
    options?: UseSlashLookupOptions,
): UseSlashLookupResult {
    const categories = options?.categories ?? DEFAULT_LOOKUP_CATEGORIES;
    const maxResults = options?.maxResults ?? DEFAULT_MAX_RESULTS;

    const token = useMemo(() => detectSlashToken(value, caretPos), [value, caretPos]);

    /** Slash-position of the token that was manually closed via Esc. Cleared as
     *  soon as the active token's slashPos differs (i.e. a *new* token starts). */
    const [closedSlashPos, setClosedSlashPos] = useState<number | null>(null);
    /** Id of the category the user has picked for the *current* token. Null means
     *  we're still in the category-picker stage. Reset every time slashPos changes. */
    const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
    /** Slash-position the activeCategoryId was set for — used to know when to
     *  reset (without coupling to `token` object identity). */
    const [activeForSlashPos, setActiveForSlashPos] = useState<number | null>(null);
    const [highlightIndex, setHighlightIndexState] = useState(0);

    // Reset category selection whenever a *new* slash token starts.
    useEffect(() => {
        if (!token) {
            if (activeCategoryId !== null) setActiveCategoryId(null);
            if (activeForSlashPos !== null) setActiveForSlashPos(null);
            return;
        }
        if (activeForSlashPos !== null && activeForSlashPos !== token.slashPos) {
            setActiveCategoryId(null);
            setActiveForSlashPos(null);
        }
    }, [token, activeCategoryId, activeForSlashPos]);

    // Auto-pick the first ("entity") category when the user starts typing chars
    // after `/` without explicitly picking one. Keeps the legacy `/cor` UX.
    useEffect(() => {
        if (!token) return;
        if (activeCategoryId !== null) return;
        if (token.query.length === 0) return;
        const fallback = categories.find((c) => c.id === 'entity') ?? categories[0];
        if (fallback) {
            setActiveCategoryId(fallback.id);
            setActiveForSlashPos(token.slashPos);
        }
    }, [token, activeCategoryId, categories]);

    // Reset highlight whenever the visible list materially changes.
    useEffect(() => {
        setHighlightIndexState(0);
    }, [token?.slashPos, token?.query, activeCategoryId]);

    // Clear closedSlashPos when token disappears or the slash moves.
    useEffect(() => {
        if (closedSlashPos !== null && (!token || token.slashPos !== closedSlashPos)) {
            setClosedSlashPos(null);
        }
    }, [token, closedSlashPos]);

    const mode: SlashLookupMode = activeCategoryId == null ? 'category' : 'search';

    const activeCategory = useMemo(
        () => (activeCategoryId == null ? null : categories.find((c) => c.id === activeCategoryId) ?? null),
        [activeCategoryId, categories],
    );

    const categoryItems = useMemo<LookupItem[]>(() => {
        if (!activeCategory) return [];
        // Static items take precedence; otherwise fall back to the runtime prop
        // (used for the API-backed `entity` category).
        return activeCategory.items ?? items ?? [];
    }, [activeCategory, items]);

    const suggestions = useMemo<ScoredMatch[]>(() => {
        if (!token || mode !== 'search') return [];
        if (categoryItems.length === 0) return [];
        return searchLookup(token.query, categoryItems, maxResults);
    }, [token, mode, categoryItems, maxResults]);

    const isActive =
        token !== null &&
        closedSlashPos !== token.slashPos &&
        (mode === 'category'
            ? categories.length > 0
            : suggestions.length > 0);

    const close = useCallback(() => {
        if (token) setClosedSlashPos(token.slashPos);
    }, [token]);

    const accept = useCallback(
        (item: LookupItem) => {
            if (!token) return null;
            const before = value.slice(0, token.range.start);
            const after = value.slice(token.range.end);
            // Insert the plain entity name — the `/` *and* the in-flight query
            // (`token.range.start..token.range.end`) are both replaced. The
            // resulting textarea content carries no slash for confirmed picks,
            // which lets the caller render the highlight pill as the only
            // visual marker.
            const insertion = item.entity_name;
            const nextValue = before + insertion + after;
            const rangeStart = before.length;
            const rangeEnd = rangeStart + insertion.length;
            const nextCaret = rangeEnd;
            // Close the dropdown for this slash position after selection.
            setClosedSlashPos(token.slashPos);
            return {
                nextValue,
                nextCaret,
                item,
                range: { start: rangeStart, end: rangeEnd },
            };
        },
        [token, value],
    );

    const pickCategory = useCallback(
        (id: string) => {
            if (!token) return;
            if (!categories.some((c) => c.id === id)) return;
            setActiveCategoryId(id);
            setActiveForSlashPos(token.slashPos);
        },
        [token, categories],
    );

    // Length used to clamp/wrap highlight depending on which list is shown.
    const visibleListLength = mode === 'category' ? categories.length : suggestions.length;

    const setHighlightIndex = useCallback(
        (idx: number) => {
            if (visibleListLength === 0) {
                setHighlightIndexState(0);
                return;
            }
            const n = visibleListLength;
            const wrapped = ((idx % n) + n) % n;
            setHighlightIndexState(wrapped);
        },
        [visibleListLength],
    );

    const clampedHighlight =
        visibleListLength === 0 ? 0 : Math.min(highlightIndex, visibleListLength - 1);

    return {
        isActive,
        mode,
        query: token?.query ?? '',
        range: token?.range ?? null,
        categories,
        activeCategoryId,
        activeCategoryLabel: activeCategory?.label ?? null,
        suggestions,
        highlightIndex: clampedHighlight,
        setHighlightIndex,
        pickCategory,
        accept,
        close,
    };
}
